import { ChevronRight, Gauge, ListChecks } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { JudgeForm } from '@/components/organisms/JudgeForm';
import type { JudgeKind } from '@/schemas/judges';
import type { JudgeDraft } from '@/services/Judges';

import { JudgeDetailLoading } from './JudgeDetail.loading';
import { useCreateJudge, useDeleteJudge, useJudge, useUpdateJudge } from './JudgeDetail.requests';
import { styles } from './JudgeDetail.styles';

export const JudgeDetail = () => {
	const { t } = useTranslation('translation', { keyPrefix: 'judgeDetail' });
	const navigate = useNavigate();
	const { id } = useParams();
	const isNew = id === 'new';
	const [pickedKind, setPickedKind] = useState<JudgeKind | null>(null);

	const { data: judge, isLoading, isError } = useJudge(id ?? '', { enabled: !!id && !isNew });
	const createMutation = useCreateJudge({ onSuccess: () => navigate('/judges') });
	const updateMutation = useUpdateJudge({ onSuccess: () => navigate('/judges') });
	const deleteMutation = useDeleteJudge({ onSuccess: () => navigate('/judges') });

	if (!isNew && isLoading) return <JudgeDetailLoading />;
	if (!isNew && (isError || !judge)) {
		return (
			<main className={styles.content}>
				<p className="text-[13px] text-error">{t('NOT_FOUND')}</p>
			</main>
		);
	}

	const handleSubmit = (draft: JudgeDraft) => {
		if (isNew) {
			createMutation.mutate(draft);
		} else if (id) {
			updateMutation.mutate({ id, patch: draft });
		}
	};

	const handleDelete = () => {
		if (id && !isNew) deleteMutation.mutate({ id });
	};

	return (
		<>
			<header className={styles.topbar}>
				<nav className="flex items-center gap-3xs text-[12px] text-subtle">
					<Link to="/judges" className="hover:text-default">
						{t('BREADCRUMB_PARENT')}
					</Link>
					<ChevronRight className="h-sm w-sm" />
					<span className="font-medium text-default">{isNew ? t('BREADCRUMB_NEW') : judge?.name}</span>
				</nav>
			</header>

			<main className={styles.content}>
				<header>
					<p className={styles.pageEyebrow}>{isNew ? t('EYEBROW_NEW') : t('EYEBROW_EDIT')}</p>
					<h1 className={styles.pageH1}>{isNew ? t('BREADCRUMB_NEW') : judge?.name}</h1>
				</header>

				{isNew && !pickedKind ? (
					<section className="flex flex-col gap-md">
						<h2 className="text-[14px] font-semibold text-default">{t('PICK_KIND_TITLE')}</h2>
						<div className="flex flex-col gap-md md:flex-row">
							<button type="button" onClick={() => setPickedKind('rubric')} className={styles.pickerCard}>
								<Gauge className="h-md w-md text-purple-700" />
								<p className="text-[14px] font-semibold text-default">{t('PICK_RUBRIC_TITLE')}</p>
								<p className="text-[12px] text-subtle">{t('PICK_RUBRIC_BODY')}</p>
							</button>
							<button type="button" onClick={() => setPickedKind('checklist')} className={styles.pickerCard}>
								<ListChecks className="h-md w-md text-purple-700" />
								<p className="text-[14px] font-semibold text-default">{t('PICK_CHECKLIST_TITLE')}</p>
								<p className="text-[12px] text-subtle">{t('PICK_CHECKLIST_BODY')}</p>
							</button>
						</div>
					</section>
				) : (
					<JudgeForm
						mode={isNew ? 'create' : 'edit'}
						initialKind={(judge?.kind ?? pickedKind ?? 'rubric') as JudgeKind}
						initialValue={judge}
						onSubmit={handleSubmit}
						onCancel={() => navigate('/judges')}
						onDelete={!isNew ? handleDelete : undefined}
						isSubmitting={createMutation.isPending || updateMutation.isPending}
						isDeleting={deleteMutation.isPending}
					/>
				)}
			</main>
		</>
	);
};
